import { Group, Text, ComboboxProps, Button } from '@mantine/core';
import { DateValue } from '@mantine/dates';
import { FC } from 'react';

import styles from './DateFilterButton.module.css';
import baseStyles from '../../filter-button/BaseFilterButton.module.css';
import { BaseFilterButton, useBaseFilterButton } from 'components/filter-button/BaseFilterButton';
import { formatDate, isDateEmpty } from './dateFilterUtils';
import { DatePicker } from 'components/dates/date-picker/DatePicker';

export type DateFilterButtonProps = {
    label: string;
    disabled?: boolean;
    value?: DateValue;
    defaultValue?: DateValue;
    submittedValue?: DateValue;
    onChange?: (value: DateValue) => void;
    onSubmit?: (value: DateValue) => void;
    showSubmitButtons?: boolean;
    size?: string;
    form?: string;
    comboboxProps?: Partial<ComboboxProps>;
    placeholder?: string;
    clearable?: boolean;
    dateFormat?: Intl.DateTimeFormatOptions;
};

export const DateFilterButton: FC<DateFilterButtonProps> = ({
    label,
    disabled,
    value,
    defaultValue,
    submittedValue,
    onChange,
    onSubmit,
    form,
    size,
    showSubmitButtons = false,
    comboboxProps,
    placeholder = 'Select date',
    clearable = true,
    dateFormat,
}) => {
    const hooks = useBaseFilterButton<DateValue>({
        value,
        defaultValue,
        submittedValue,
        onChange,
        onSubmit,
        finalValue: null,
    });

    const { value: _value, setValue, submittedValue: _submittedValue } = hooks;

    const hasActiveFilters = !isDateEmpty(_submittedValue);

    const handleDateChange = (date: DateValue) => {
        setValue(date);
    };

    const handleClear = () => {
        setValue(null);
    };

    const hiddenInputs = (
        <input
            type="hidden"
            name={form ? `${form}-date` : undefined}
            value={formatDate(_value, dateFormat)}
            disabled={disabled}
        />
    );

    return (
        <BaseFilterButton
            label={label}
            disabled={disabled}
            size={size}
            showSubmitButtons={showSubmitButtons}
            comboboxProps={{
                middlewares: { flip: false, shift: false },
                ...comboboxProps,
            }}
            hasActiveFilters={hasActiveFilters}
            dropdownClassName={styles.dropdown}
            triggerClassName={styles.trigger}
            badgeClassName={baseStyles.dot}
            hooks={hooks}
            hiddenInputs={hiddenInputs}
        >
            <Group align="center" gap="sm" wrap="nowrap" justify="space-between" className={styles.topGroup}>
                <Text size="sm" className={styles.displayValue}>
                    {isDateEmpty(_value) ? placeholder : formatDate(_value, dateFormat)}
                </Text>
                {clearable && hasActiveFilters && (
                    <Button
                        type="button"
                        onClick={handleClear}
                        className={styles.clearButton}
                        aria-label="Clear date"
                    >
                        Clear
                    </Button>
                )}
            </Group>

            <div className={styles.datePickerWrapper}>
                <DatePicker type="default" value={_value} onChange={handleDateChange} aria-label={label} />
            </div>
        </BaseFilterButton>
    );
};
